/**
 * ZAdminOrgConnections - Zod Validation Schemas for Admin Org Connections API
 *
 * Admin endpoints for viewing and patching ApiKeyConnection records per organization.
 * Credentials are decrypted before being returned (admin-only access).
 *
 * All endpoints are PBAC-protected (admin:organizations).
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { ROUTE_SENSITIVITY } from "../../route-sensitivity";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Single connection shape returned to the admin panel.
 * Dates are serialized to ISO strings. Credentials are decrypted.
 */
export const AdminOrgConnectionSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  service: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  status: z.string(),
  failureReason: z.string().nullable(),
  lastValidatedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastUsedAt: z.string().nullable(),
  /** Decrypted credential key-value pairs (all values coerced to string) */
  credentials: z.record(z.string(), z.string()),
  /** Decrypted settings blob (nullable when not set) */
  settings: z.record(z.string(), z.unknown()).nullable(),
  deploymentsCount: z.number().int(),
  phoneNumbersCount: z.number().int(),
  whatsappSendersCount: z.number().int()
});

export type AdminOrgConnection = z.infer<typeof AdminOrgConnectionSchema>;

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const AdminOrgConnectionsListResponseSchema = z.object({
  connections: z.array(AdminOrgConnectionSchema)
});

export const AdminOrgConnectionUpdateResponseSchema = z.object({
  success: z.boolean()
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const AdminOrgConnectionUpdateCredentialsBodySchema = z.object({
  credentials: z.record(z.string(), z.string())
});

export const AdminOrgConnectionUpdateSettingsBodySchema = z.object({
  settings: z.record(z.string(), z.unknown())
});

// ═══════════════════════════════════════════════════════════════
// PATH VARS SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const AdminOrgConnectionsListPathVarsSchema = z.object({
  orgId: z.string().min(1)
});

export const AdminOrgConnectionPathVarsSchema = z.object({
  orgId: z.string().min(1),
  connectionId: z.string().min(1)
});

// ═══════════════════════════════════════════════════════════════
// ZADMINORGCONNECTIONS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZAdminOrgConnections = {
  /**
   * List all connections for an organization (decrypted)
   * GET /api/admin/organizations/:orgId/connections
   */
  ListConnections: {
    method: "GET" as const,
    path: "/api/admin/organizations/:orgId/connections",
    // Returns every connection's credentials FULLY DECRYPTED — the use case runs
    // `safeGetCredentials` and coerces each value to a plain string. The panel
    // needs exactly that: it renders the values in an editable table and saves
    // them back through `UpdateCredentials`. So the response is right, and the
    // route must simply never become a tool.
    sensitivity: ROUTE_SENSITIVITY.SECRET,
    PathVars: AdminOrgConnectionsListPathVarsSchema,
    Response: ApiSuccessSchema(AdminOrgConnectionsListResponseSchema)
  },

  /**
   * Patch credentials for a specific connection (merge + re-encrypt)
   * PATCH /api/admin/organizations/:orgId/connections/:connectionId/credentials
   */
  UpdateCredentials: {
    method: "PATCH" as const,
    path: "/api/admin/organizations/:orgId/connections/:connectionId/credentials",
    // Its RESPONSE is only `{ success }`, but its BODY is the credential itself,
    // and a staged write proposal persists the request body and renders it on
    // the confirm card. That is the same storage and the same transcript as a
    // read — so the marker covers it too. The panel keeps the endpoint; only the
    // agent loses it.
    sensitivity: ROUTE_SENSITIVITY.SECRET,
    PathVars: AdminOrgConnectionPathVarsSchema,
    Body: AdminOrgConnectionUpdateCredentialsBodySchema,
    Response: ApiSuccessSchema(AdminOrgConnectionUpdateResponseSchema)
  },

  /**
   * Patch settings for a specific connection
   * PATCH /api/admin/organizations/:orgId/connections/:connectionId/settings
   */
  UpdateSettings: {
    method: "PATCH" as const,
    path: "/api/admin/organizations/:orgId/connections/:connectionId/settings",
    PathVars: AdminOrgConnectionPathVarsSchema,
    Body: AdminOrgConnectionUpdateSettingsBodySchema,
    Response: ApiSuccessSchema(AdminOrgConnectionUpdateResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════

export type AdminOrgConnectionsListPathVars = z.infer<typeof AdminOrgConnectionsListPathVarsSchema>;
export type AdminOrgConnectionPathVars = z.infer<typeof AdminOrgConnectionPathVarsSchema>;
export type AdminOrgConnectionsListResponse = z.infer<typeof AdminOrgConnectionsListResponseSchema>;
export type AdminOrgConnectionUpdateResponse = z.infer<
  typeof AdminOrgConnectionUpdateResponseSchema
>;
export type AdminOrgConnectionUpdateCredentialsBody = z.infer<
  typeof AdminOrgConnectionUpdateCredentialsBodySchema
>;
export type AdminOrgConnectionUpdateSettingsBody = z.infer<
  typeof AdminOrgConnectionUpdateSettingsBodySchema
>;
